import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAttempt, msSinceLastProgress, startAttempt } from '../../services/analytics/telemetry';
import { useAppDispatch, useTypedSelector } from '../../store/store';
import {
  processRecord,
  reset as resetUploadSlice,
  resetProcessRecord,
  setMimeType,
  setPendingUploadedStory,
  // setProgress,
  setUploadError,
  setIsPausedForWifi,
  // CreateStoryRequest,
} from '../../store/slices/upload';
import { patch as patchPendingUpload } from '../../services/upload/pendingUploadStore';
import { getUploadDimensions } from '../../services/upload/uploadDimensions';
import { getDefaultStoryDetails, getSupportedMimeTypes, getUserStoryDetails } from './utils';
import { ErrorsStrings } from '../../common/localization/en';
// import { AppRoutes } from '../../common/constants/routes';
import { DEFAULT_STORY_DETAILS } from '../../api/constants';
import { ErrorDialog } from '../../common/components/ErrorDialog/ErrorDialog';
import { isSafari } from 'react-device-detect';
import { CreateVideo } from '../../common/components/CreateVideo/CreateVideo';
import { EventNames, maxSizeUploadedUserFile } from '../../common/constants/constants';
import { useClientType } from '../../services/hooks/useClientType';
import { useHandleSubmitUploadedFile } from '../../services/hooks/video-handlers/useHandleSubmitUploadedFile';
import { WakeOutcome, WakeReason } from '../../services/hooks/video-handlers/Upload';
import { RecordVideoComponent } from './RecordVideoComponent/RecordVideoComponent';
import { Wrapper } from './VideoToolPage.helper';
import { useTrackEvent } from '../../common/hooks/useTrackEvent';
import { RecordErrorModal } from '../../common/components/RecordErrorModal/RecordErrorModal';
import { usePrevious } from '../../services/hooks/usePrevious';
import { UploadProcessStep } from '../../store/storeModels';
import { VideoUploadingPage } from '../VideoUploadingPage/VideoUploadingPage';
import VerificationScreen from '../VerificationScreen/VerificationScreen';
import { GetUserQuestions, setSignUpFlowActiveTabIndex } from '../../store/slices/ui';
// import { StoryVideosApiModel } from '../../api/models/videos';

export type RestartTrigger = 'user' | 'auto' | 'online_event' | 'visibilitychange' | 'retry_timer';

const WAKE_REASON: Record<RestartTrigger, WakeReason> = {
  user: 'user',
  auto: 'retry_timer',
  online_event: 'online',
  visibilitychange: 'visible',
  retry_timer: 'retry_timer',
};
const WAKE_ACTED: ReadonlySet<WakeOutcome> = new Set(['woke_backoff', 'woke_retry', 'aborted_stalled_part']);

interface Props {
  isButtonsDisabled: boolean;
  isRecordVideoScreen: boolean;
  setIsRecordVideoScreen: (isRecordScreen: boolean) => void;
  onUploadVideoStart: () => Promise<boolean>;
  captchaResponse: string;
  /** File selected externally (e.g. from HomeScreen). Triggers upload when set. */
  externalFile?: File | null;
  /** When true, hides the default CreateVideo UI (HomeScreen replaces it). */
  hideCreateVideo?: boolean;
  /**
   * When provided alongside `externalFile`, resume an existing GCS session
   * rather than minting a new one. SignUpScreen sets this after the user
   * picks the same file via the resume sheet and the fingerprint matches.
   */
  resumeFrom?: { id: string; uploadUrl: string } | null;
}

export const VideoToolPage: FC<Props> = ({
  isButtonsDisabled,
  isRecordVideoScreen,
  setIsRecordVideoScreen,
  onUploadVideoStart,
  captchaResponse,
  externalFile,
  hideCreateVideo,
  resumeFrom,
}) => {
  const [blob, setBlob] = useState<undefined | string>();
  const [recordRender, setRecordRender] = useState(Date.now());
  const [file, setFile] = useState<null | File>(null);
  const [storyId, setStoryId] = useState('');
  const [isErrorAlert, setIsErrorAlert] = useState(false);

  const dispatch = useAppDispatch();

  const uploadingFileRef = useRef<File | null>(null);
  const { currentStep, isUploadError, isPausedForWifi, uploadingProgress } = useTypedSelector((state) => state.upload);
  // Refs so async .then callbacks (handleStoryUpload, handleVideoUploadRestart)
  // can read the live pause/progress flags without re-binding on every change.
  // `undefined` from the upload helpers can mean either "paused", "already
  // complete", or "errored"; without these refs we'd blanket-set isUploadError
  // and flash the Dipped banner ("Upload paused. We'll automatically retry…")
  // after a successful pause → resume → complete cycle, or after foregrounding
  // the tab post-completion.
  const isPausedForWifiRef = useRef(isPausedForWifi);
  isPausedForWifiRef.current = isPausedForWifi;
  const uploadingProgressRef = useRef(uploadingProgress);
  uploadingProgressRef.current = uploadingProgress;

  const {
    defaultVideoDetails,
    returnUrl,
    displayName,
    name,
    phoneNumber,
    id: userId,
  } = useTypedSelector((state) => state.me);
  const { account, accountAttributes } = useTypedSelector((state) => state.account);

  const [isRecordError, setIsRecordError] = useState(false);

  const [camera, setCamera] = useState<'user' | 'environment'>('user');
  const previousCameraOption = usePrevious(camera);
  // const [isOnlineRegister, setIsOnlineRegistered] = useState(false);

  const { trackEvent } = useTrackEvent();

  useEffect(() => {
    if (userId) {
      dispatch(GetUserQuestions(userId));
    }
  }, [userId]);

  useEffect(() => {
    // if phone is not verified -> user will be redirected to story page from phone-verification screen
    if (storyId && phoneNumber) {
      // history.push(`${AppRoutes.Share}/${storyId}`);
      dispatch(setSignUpFlowActiveTabIndex(0));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId, phoneNumber]);
  const { supportedMimeType, pendingUploadedStory, sendSMSNotification } = useTypedSelector((state) => state.upload);
  const { isAthleticSolutionsClient } = useClientType();

  const phoneVerifiedRef = useRef(phoneNumber);
  const smsNotificationRef = useRef(sendSMSNotification);
  const pendingStoryRef = useRef(pendingUploadedStory);

  // (Removed) The legacy service-worker upload retry listener used to live
  // here, dispatching state on UPDATE_FROM_SW{,_RESUME_UPLOAD} messages.
  // Nothing in the app fed the SW's IndexedDB upload queue, and the SW had
  // a hardcoded dev API URL anyway. All resumable-upload behaviour now runs
  // page-side via `Upload.ts` + the `pendingUpload` localStorage record.

  useEffect(() => {
    phoneVerifiedRef.current = phoneNumber;
    smsNotificationRef.current = sendSMSNotification;
    pendingStoryRef.current = pendingUploadedStory;
  }, [phoneNumber, sendSMSNotification, pendingUploadedStory]);

  const storyDetails = useMemo(() => {
    const properties = getDefaultStoryDetails(
      defaultVideoDetails,
      accountAttributes.properties?.['webapp.default-story-details'],
      DEFAULT_STORY_DETAILS(),
      returnUrl,
    );
    const userName = displayName || name;
    return getUserStoryDetails(properties, userName, account.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayName, name, account, defaultVideoDetails, accountAttributes]);

  useEffect(() => {
    if (file && file.size >= maxSizeUploadedUserFile) {
      setIsErrorAlert(true);
      trackEvent({
        action: EventNames.FileSizeLimitError,
        size: file.size,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setIsErrorAlert, file]);

  useEffect(() => {
    if (!isSafari) {
      const mimeType = getSupportedMimeTypes()[0];
      dispatch(setMimeType(mimeType));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const {
    handleUploadedFile: uploadStory,
    handleSubmitVideo,
    postStory,
    restartUpload,
    wakeUpload,
    resumableUpload,
    storyPostedRef,
  } = useHandleSubmitUploadedFile(setStoryId, isAthleticSolutionsClient, returnUrl);

  // reset upload slice on unmount
  useEffect(() => {
    dispatch(resetUploadSlice());
    return () => {
      dispatch(resetUploadSlice());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-entrancy guard for the externalFile effect.
  //
  // The manual reCAPTCHA fallback in SignUpScreen.onCaptchaChange intentionally
  // cycles `externalFile` (null → file) to re-fire this effect after the user
  // completes the captcha. But reCAPTCHA Enterprise also fires onChange when
  // it refreshes its token *during* an in-flight upload, which would otherwise
  // spawn a fresh `loginUnverified` + `createResumableUploadUrl` + full GCS
  // upload session each time. Observed in the wild: 4 parallel upload sessions
  // (4 distinct userIds) in ~750 ms for a single file pick.
  //
  // Ref-based gate: synchronous, immune to the React-state-propagation race
  // that `state.upload.isUploadInprogress` suffers from. Reset on auth failure
  // so the manual-captcha retrigger still works as intended.
  const uploadInitiatedRef = useRef(false);

  // Handle file selected externally (from HomeScreen)
  useEffect(() => {
    if (externalFile && !uploadInitiatedRef.current) {
      uploadInitiatedRef.current = true;
      (async () => {
        trackEvent({ action: EventNames.Upload_Initiated, ...getUploadDimensions() });
        trackEvent({
          action: EventNames.preflight_start,
          trigger: resumeFrom ? 'resume_cross_session' : getAttempt()?.path === 'camera' ? 'camera' : 'picker',
          file_size: externalFile.size,
          resumes_attempt_id: getAttempt()?.resumesAttemptId ?? null,
        });
        uploadingFileRef.current = externalFile;
        let success = false;
        try {
          success = await onUploadVideoStart();
        } catch {
          success = false;
        }
        if (success) {
          handleStoryUpload(externalFile, { webappRecorded: false, resumeFrom: resumeFrom ?? undefined });
        } else {
          // Auth failed (e.g., manual captcha required) — release the guard so
          // the captcha-completion retrigger can drive a fresh attempt.
          uploadInitiatedRef.current = false;
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalFile]);

  useEffect(() => {
    setTimeout(async () => {
      if (blob) {
        const fileBlob = await fetch(blob).then((res) => res.blob());
        if (fileBlob.size >= maxSizeUploadedUserFile) {
          setIsErrorAlert(true);
          trackEvent({ action: EventNames.FileSizeLimitError, size: fileBlob.size });
        } else {
          const webappRecorded = true;
          startAttempt('recorder', fileBlob.size);
          trackEvent({
            action: EventNames.record_submit,
            file_size: fileBlob.size,
            mime: fileBlob.type || null,
            duration_s: getAttempt()?.recordedDurationS ?? null,
          });
          trackEvent({ action: EventNames.preflight_start, trigger: 'recorder', file_size: fileBlob.size });
          let success;
          try {
            success = await onUploadVideoStart();
          } catch (err) {
            console.log('error caught here');
            trackEvent({
              action: EventNames.Upload_Failed,
              accountId: account.id,
              errorMessage: JSON.stringify(err),
            });
            dispatch(setUploadError(true));
          }

          if (success) {
            handleSubmitVideo(blob, storyDetails, true, supportedMimeType, smsNotificationRef, webappRecorded);
            trackEvent({ action: EventNames.SubmitRecordedVideo, size: fileBlob.size });
          }
        }
      }
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blob]);

  useEffect(() => {
    if (isRecordVideoScreen) {
      dispatch(processRecord());
    }
  }, [isRecordVideoScreen, dispatch, processRecord]);

  const remount = useCallback(() => {
    setRecordRender((d) => d + 1);
    setIsRecordVideoScreen(false);
    dispatch(resetProcessRecord());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setRecordRender]);

  const refreshRecorder = useCallback(() => {
    setRecordRender((d) => d + 1);
    setIsRecordVideoScreen(false);
    setIsRecordVideoScreen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setRecordRender]);

  const resetVideo = () => {
    setIsErrorAlert(false);
    setFile(null);
    refreshRecorder();
  };

  const handleStoryUpload = useCallback(
    async (
      file: File | null,
      { webappRecorded, resumeFrom }: { webappRecorded: boolean; resumeFrom?: { id: string; uploadUrl: string } },
    ) => {
      uploadStory({
        file,
        details: storyDetails,
        isPublic: true,
        videoPos: { x: 0, y: 0, width: 0 },
        shouldPostStory: false,
        webappRecorded,
        resumeFrom,
      }).then((story) => {
        if (!story) {
          // Undefined return is ambiguous: pause, "already complete"
          // (restartUpload's `isUploadCompleted` short-circuit on a post-
          // success restart trigger), or a real internal failure (which
          // already dispatched the right error/fatal flag). Suppress the
          // error dispatch in the first two cases.
          if (isPausedForWifiRef.current) return;
          if (uploadingProgressRef.current >= 100) return;
          dispatch(setUploadError(true));
          return;
        }
        if (storyPostedRef.current) return;
        storyPostedRef.current = true;

        const storyWithNotification = { ...story, sendSMSNotification: smsNotificationRef.current };
        postStory(storyWithNotification).then(() => {
          uploadingFileRef.current = null;
        });

        if (!phoneVerifiedRef.current) {
          dispatch(setPendingUploadedStory(storyWithNotification));
        }
      });
    },
    [sendSMSNotification],
  );

  // `trigger` separates a real user tap ('user') from the automated recovery
  // paths ('auto': the 5s error-retry loop, `online`, and visibilitychange
  // handlers in UploadFlowCarousel). resume_button_click is documented as a
  // USER action — an AI-driven stall test caught the auto-loop logging it
  // every 5 seconds (18 phantom "button clicks" in one wedged run, including
  // attempts the isUploadInProgress guard then blocked). Auto attempts are
  // already observable via upload_watchdog_recovered / upload_interrupted.
  const handleVideoUploadRestart = async (trigger: RestartTrigger = 'auto') => {
    console.log('[UploadResume] handleVideoUploadRestart called, trigger:', trigger);
    if (trigger === 'user') {
      trackEvent({ action: EventNames.resume_button });
    }
    const isUploadInProgress = localStorage.getItem('isUploadInProgress');
    console.log('[UploadResume] isUploadInProgress guard:', isUploadInProgress, 'userId:', userId);
    const msSinceProgress = msSinceLastProgress();
    // The lock means the engine's own retry loop still holds this upload, so
    // a restart would be refused. Nudge the running upload instead: it cuts
    // a retry backoff short, or replaces a part the OS killed while the page
    // was hidden or offline. Also called when the lock is free, so the return
    // is timed (upload_progress_after_return) on the restart path too.
    const wakeOutcome = await wakeUpload(WAKE_REASON[trigger]);
    // One event for every recovery path, with whether it actually acted.
    trackEvent({
      action: EventNames.upload_recovery_triggered,
      trigger: trigger === 'user' ? 'user_tap' : trigger === 'auto' ? 'retry_timer' : trigger,
      acted: !isUploadInProgress || WAKE_ACTED.has(wakeOutcome),
      wake_outcome: wakeOutcome,
      ms_since_last_progress: msSinceProgress,
    });
    if (isUploadInProgress) {
      console.log('[UploadResume] blocked by isUploadInProgress guard');
      return;
    }
    if (!userId) {
      let success;
      try {
        success = await onUploadVideoStart();
      } catch (err) {
        trackEvent({
          action: EventNames.Upload_Failed,
          accountId: account.id,
          errorMessage: JSON.stringify(err),
        });
        dispatch(setUploadError(true));
      }

      if (success) {
        dispatch(setUploadError(false));
        restartUpload().then((createStoryRequest) => {
          if (!createStoryRequest) {
            // Pause + already-complete are benign no-results; only real
            // failures should set error.
            if (isPausedForWifiRef.current) return;
            if (uploadingProgressRef.current >= 100) return;
            dispatch(setUploadError(true));
            return;
          }
          if (storyPostedRef.current) return;
          storyPostedRef.current = true;
          const storyWithNotification = { ...createStoryRequest, sendSMSNotification: smsNotificationRef.current };
          postStory(storyWithNotification).then(() => {
            uploadingFileRef.current = null;
          });

          if (!phoneVerifiedRef.current) {
            dispatch(setPendingUploadedStory(storyWithNotification));
          }
        });
      }
      return;
    }
    dispatch(setUploadError(false));
    restartUpload().then((createStoryRequest) => {
      if (!createStoryRequest) {
        if (isPausedForWifiRef.current) return;
        if (uploadingProgressRef.current >= 100) return;
        dispatch(setUploadError(true));
        return;
      }
      if (storyPostedRef.current) return;
      storyPostedRef.current = true;
      const storyWithNotification = { ...createStoryRequest, sendSMSNotification: smsNotificationRef.current };
      postStory(storyWithNotification).then(() => {
        uploadingFileRef.current = null;
      });

      if (!phoneVerifiedRef.current) {
        dispatch(setPendingUploadedStory(storyWithNotification));
      }
    });

    // handleStoryUpload(uploadingFileRef.current, { webappRecorded: false });
  };

  useEffect(() => {
    if (camera && previousCameraOption && camera !== previousCameraOption) {
      setIsRecordVideoScreen(true);
    }
  }, [camera, previousCameraOption]);

  if (Object.keys(accountAttributes.properties).length === 0) {
    return <></>;
  }

  if (currentStep === UploadProcessStep.UploadVideo) {
    return (
      <Wrapper>
        <ErrorDialog errorMessage={ErrorsStrings.FileSizeExceeded} isOpen={isErrorAlert} onClose={resetVideo} />
        <RecordErrorModal
          isOpen={isRecordError}
          onClose={() => {
            setIsRecordError(false);
          }}
        />

        {isRecordVideoScreen ? (
          <>
            <RecordVideoComponent
              key={recordRender}
              setBlob={setBlob}
              setFile={setFile}
              onFileChange={(newFile) => {
                trackEvent({ action: EventNames.Upload_Initiated, ...getUploadDimensions() });
                if (newFile) {
                  startAttempt('picker', newFile.size);
                  trackEvent({ action: EventNames.preflight_start, trigger: 'picker', file_size: newFile.size });
                }
                uploadingFileRef.current = newFile;
                handleStoryUpload(newFile, { webappRecorded: true });
              }}
              handleRemount={remount}
              setStoryId={setStoryId}
              onRefresh={refreshRecorder}
              setIsRecordError={setIsRecordError}
              camera={camera}
              toggleCamera={() => {
                setCamera(camera === 'user' ? 'environment' : 'user');
              }}
            />
          </>
        ) : hideCreateVideo ? null : (
          <>
            <CreateVideo
              file={file}
              setShowRecord={setIsRecordVideoScreen}
              isButtonsDisabled={isButtonsDisabled}
              onFileChange={async (newFile) => {
                trackEvent({ action: EventNames.Upload_Initiated, ...getUploadDimensions() });
                uploadingFileRef.current = newFile;
                const success = await onUploadVideoStart();
                if (success) {
                  handleStoryUpload(newFile, { webappRecorded: false });
                }
              }}
            />
          </>
        )}
      </Wrapper>
    );
  }

  // In CAV6 flow (hideCreateVideo=true), identity is collected via IdentityBlock before upload.
  // Only show VerificationScreen for the legacy flow.
  if (!hideCreateVideo && !phoneNumber && !isUploadError) {
    return (
      <>
        <VerificationScreen captchaResponse={captchaResponse} />
      </>
    );
  }

  // Click-time tracking for both lives in UploadFlowCarousel.handleSheetWifiLater
  // / handleSheetResume (one event per click). These handlers just do the work.
  const handlePauseForWifi = () => {
    // `Upload.pause()` now aborts the in-flight chunk PUT and the post-bytes
    // path catches UploadPausedError before posting the story — so honoring
    // a Wi-Fi-later click is safe at any progress level, including the
    // last-chunk race that used to slip through and create a broken story.
    resumableUpload?.pause();
    dispatch(setIsPausedForWifi(true));
    patchPendingUpload({ status: 'paused_wifi' });
    // Clear the in-progress guard synchronously here. The UploadPausedError
    // catch in handleUploadedFile also clears it, but that runs on microtask
    // drain — on Android, Chrome can freeze JS execution between this click
    // and that catch, leaving the flag stuck at 'true'. A stuck flag silently
    // blocks restartUpload's first guard ([useHandleSubmitUploadedFile.tsx:134]),
    // which manifests as "tapped Resume, no meter, no upload."
    localStorage.setItem('isUploadInProgress', '');
  };

  const handleResumeFromWifi = () => {
    dispatch(setIsPausedForWifi(false));
    patchPendingUpload({ status: 'in_progress' });
    // Belt-and-braces: even if pause's sync clear was somehow missed (frozen
    // mid-call, prior version of the code persisted state, etc.), make sure
    // the flag is clear before restart so we never silent-bail on a resume.
    localStorage.setItem('isUploadInProgress', '');
    // Re-use the existing restart path — Upload.start() will resume via the
    // 308 offset probe against the same session URI. This one IS a user
    // action (the "Resume upload" tap on the Wi-Fi sheet).
    handleVideoUploadRestart('user');
  };

  if (
    [UploadProcessStep.VideoUploading, UploadProcessStep.Uploaded, UploadProcessStep.PrepareVideo].includes(currentStep)
  ) {
    return (
      <VideoUploadingPage
        onRestartClick={handleVideoUploadRestart}
        onPauseForWifi={handlePauseForWifi}
        onResumeFromWifi={handleResumeFromWifi}
      />
    );
  }

  return <></>;
};
